import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { formatLeadSource } from '../../lib/leadSource';
import { User, Building2, Phone, Mail, Loader2, ChevronDown, MoreVertical, Eye, Zap, Sparkles, Star, TrendingUp, ArrowRight, UserPlus, ChevronLeft, ChevronRight, Target, Activity, Lock, Unlock, Plus, AlertTriangle, Tag, Check } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { formatLocal } from '../../lib/dateUtils';
import { toast } from 'sonner';
import api from '../../lib/api';

const LeadKanbanView = React.memo(function LeadKanbanView({
  view,
  kanbanData,
  pipelineStages,
  statusConfigs,
  sortedLeads,
  onDragEnd,
  handleViewDetails,
  handleRevealContact,
  loadMoreColumn,
  loadingMoreColumn,
  setQuickActionLead,
  setEmailDialogOpen,
  setCallDialogOpen,
  openEditSheet,
  openLeadDetail,
  teamUsers,
  currency,
  checkKanbanScroll,
  handleKanbanDragOver,
  visibleStatuses,
  dragOverColumn,
  draggedLead,
  handleColumnDragEnter,
  handleColumnDragLeave,
  handleDrop,
  handleDragStart,
  activeCallLeadId,
  leadSummaries,
  callPolling,
  canScrollLeft,
  canScrollRight,
  handleDragOver,
  highlightedLeadId,
  highlightedCardRef,
  revealingLeadId,
  onLeadUpdated,
  selectedLeadIds,
  toggleSelectLead,
}) {
  const navigate = useNavigate();
  const kanbanScrollRef = useRef(null);
  const [reviewBusy, setReviewBusy] = React.useState(null); // lead.id of in-flight action
  const [demotionBusy, setDemotionBusy] = React.useState(null); // 8.7 approve/decline

  // Phase 8.7 — rep resolves the AI's pending Lost/Disqualified recommendation.
  const resolveDemotion = async (lead, action, e) => {
    if (e) e.stopPropagation();
    setDemotionBusy(lead.id);
    try {
      const r = await api.post(`/api/leads/${lead.id}/demotion?action=${action}`);
      toast.success(r.data?.message || (action === 'approve' ? 'Lead moved to Lost' : 'Kept in pipeline'));
      onLeadUpdated && onLeadUpdated();
    } catch (err) {
      toast.error('Action failed', { description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setDemotionBusy(null);
    }
  };

  // ── Bronze quick-action handlers (Iter 61) ──
  // Approve: flip needs_review=false → cadence executor resumes outreach.
  // Reject: mark lead as Disqualified (kept for audit; user can hard-delete later).
  const handleQuickApprove = async (lead, e) => {
    e?.stopPropagation();
    if (reviewBusy) return;
    setReviewBusy(lead.id);
    try {
      await api.post(`/api/leads/${lead.id}/approve-cadence`);
      toast.success(`Outreach resumed for ${lead.name || 'lead'}`);
      if (onLeadUpdated) onLeadUpdated(lead.id, { needs_review: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to approve cadence');
    } finally {
      setReviewBusy(null);
    }
  };

  const handleQuickReject = async (lead, e) => {
    e?.stopPropagation();
    if (reviewBusy) return;
    setReviewBusy(lead.id);
    try {
      await api.put(`/api/leads/${lead.id}`, { status: 'Disqualified', pipeline_stage: 'Disqualified' });
      toast.success(`${lead.name || 'Lead'} marked Disqualified`);
      if (onLeadUpdated) onLeadUpdated(lead.id, { status: 'Disqualified', pipeline_stage: 'Disqualified' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update lead');
    } finally {
      setReviewBusy(null);
    }
  };

  useEffect(() => {
    const container = kanbanScrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkKanbanScroll);
      checkKanbanScroll();
      return () => container.removeEventListener('scroll', checkKanbanScroll);
    }
  }, [checkKanbanScroll, view]);

  const scrollKanban = (direction) => {
    const container = kanbanScrollRef.current;
    if (container) {
      const scrollAmount = 320;
      container.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };
  
  if (view !== 'kanban') return null;

  return (
    <>
      {/* Kanban View - Matching Ticket Kanban Style */}
      {view === 'kanban' && (
        <div className="relative" data-testid="kanban-board">
          {/* Left Arrow - Show when scrolled right */}
          {canScrollLeft && (
            <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center pointer-events-none kanban-fade-left" style={{ width: '48px' }}>
              <Button
                variant="outline"
                size="icon"
                className="pointer-events-auto ml-1 h-9 w-9 rounded-full bg-background shadow-lg border hover:bg-muted"
                onClick={() => scrollKanban('left')}
                data-testid="kanban-scroll-left"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          {/* Right Arrow - Show when more columns available */}
          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-0 z-10 flex items-center justify-end pointer-events-none kanban-fade-right" style={{ width: '48px' }}>
              <Button
                variant="outline"
                size="icon"
                className="pointer-events-auto mr-1 h-9 w-9 rounded-full bg-background shadow-lg border hover:bg-muted"
                onClick={() => scrollKanban('right')}
                data-testid="kanban-scroll-right"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          {/* Kanban Container */}
          <div 
            ref={kanbanScrollRef}
            className="flex overflow-x-auto border rounded-lg bg-muted/30 kanban-scroll-container"
            onScroll={checkKanbanScroll}
            onDragOver={handleKanbanDragOver}
            style={{ 
              scrollBehavior: 'smooth',
              scrollSnapType: 'x proximity',
              height: 'calc(100vh - 180px)',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {pipelineStages.filter(statusObj => statusObj.visible !== false && visibleStatuses.includes(statusObj.name)).map((statusObj, index) => {
              // Use server-paginated kanbanData when available, else fallback to client-side filter
              const colData = kanbanData?.columns?.[statusObj.name];
              const statusLeads = colData ? colData.leads : sortedLeads.filter(lead => {
                const leadStatus = (lead.status || '').toLowerCase().trim();
                const columnStatus = statusObj.name.toLowerCase().trim();
                if (leadStatus === columnStatus) return true;
                if (columnStatus === 'new lead' && (leadStatus === 'new' || leadStatus === 'new lead' || !leadStatus)) return true;
                return false;
              });
              const columnTotal = colData?.total ?? statusLeads.length;
              const columnHasMore = colData?.has_more ?? false;
              const unrevealedInColumn = statusLeads.filter(l => !l.contact_revealed && !l.email && !l.phone && (l.apollo_id || l.provider_id || (l.source || '').toLowerCase().includes('apollo'))).length;
              const isDropTarget = dragOverColumn === statusObj.name;
              const canDrop = draggedLead && draggedLead.status !== statusObj.name;
              
              return (
                <div 
                  key={statusObj.id} 
                  className={`flex-shrink-0 flex flex-col bg-background transition-all duration-200 ${index !== 0 ? 'border-l border-border' : ''}`}
                  style={{ 
                    width: 'calc(100% / 4.3)',
                    minWidth: '260px',
                    scrollSnapAlign: 'start',
                    height: '100%',
                  }}
                >
                <div 
                  className={`flex flex-col h-full p-3 transition-all duration-200 ${
                    isDropTarget ? 'bg-primary/5' : ''
                  }`}
                  data-testid={`kanban-column-${statusObj.name}`}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleColumnDragEnter(e, statusObj.name)}
                  onDragLeave={(e) => handleColumnDragLeave(e, statusObj.name)}
                  onDrop={(e) => handleDrop(e, statusObj.name)}
                >
                  {/* Column Header */}
                  <div 
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg mb-3 transition-all duration-200 ${
                      isDropTarget ? 'ring-2 ring-primary shadow-md scale-[1.02]' : canDrop ? 'ring-1 ring-primary/30' : ''
                    }`}
                    style={{ backgroundColor: `${statusObj.color}15` }}
                  >
                    <div className="flex items-center gap-2">
                      <span 
                        className="font-semibold text-sm"
                        style={{ color: statusObj.color }}
                      >
                        {statusObj.name}
                      </span>
                      <Badge 
                        className="text-xs font-medium transition-transform duration-200"
                        style={{ 
                          backgroundColor: statusObj.color, 
                          color: 'white',
                          transform: isDropTarget ? 'scale(1.1)' : 'scale(1)'
                        }}
                      >
                        {columnTotal}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Cards Container - Full height */}
                  <div 
                    className={`flex-1 space-y-3 overflow-y-auto p-2 rounded-lg border-2 border-dashed transition-all duration-200 ${
                      isDropTarget 
                        ? 'border-primary bg-primary/10 shadow-inner' 
                        : canDrop 
                          ? 'border-primary/20 bg-muted/30' 
                          : 'border-transparent bg-muted/30 hover:border-primary/10'
                    }`}
                    style={{ 
                      minHeight: 'calc(100% - 60px)',
                      maxHeight: 'calc(100vh - 280px)',
                    }}
                  >
                    {statusLeads.length === 0 ? (
                      <div className={`flex flex-col items-center justify-center h-full min-h-[300px] text-muted-foreground transition-all duration-200 ${
                        isDropTarget ? 'scale-105' : ''
                      }`}>
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-200 ${
                          isDropTarget ? 'bg-primary/20 scale-110' : 'bg-muted/50'
                        }`}>
                          <Plus className={`h-5 w-5 transition-colors ${isDropTarget ? 'text-primary' : ''}`} />
                        </div>
                        <p className={`text-sm font-medium transition-colors ${isDropTarget ? 'text-primary' : ''}`}>
                          {isDropTarget ? 'Drop here' : 'No leads'}
                        </p>
                        <p className="text-xs mt-1">
                          {isDropTarget ? `Move to ${statusObj.name}` : 'Drag leads here or add new'}
                        </p>
                      </div>
                    ) : (
                      statusLeads.map((lead) => {
                        const isHighlighted = highlightedLeadId === lead.id;
                        const isBeingDragged = draggedLead?.id === lead.id;
                        const isSelected = selectedLeadIds ? selectedLeadIds.has(lead.id) : false;
                        return (
                        <Card
                          key={lead.id}
                          ref={isHighlighted ? highlightedCardRef : null}
                          draggable
                          onDragStart={(e) => handleDragStart(e, lead)}
                          onDragEnd={onDragEnd}
                          className={`group relative transition-all duration-200 bg-card cursor-move ${
                            isBeingDragged 
                              ? 'opacity-50 scale-95 shadow-lg ring-2 ring-primary' 
                              : 'hover:shadow-md hover:scale-[1.01]'
                          } ${
                            isSelected
                              ? 'ring-2 ring-blue-500 border-blue-500'
                              : isHighlighted 
                              ? 'border-2 border-dashed border-primary shadow-lg ring-2 ring-primary/20' 
                              : 'border'
                          }`}
                          data-testid={`lead-card-${lead.id}`}
                        >
                          {toggleSelectLead && (
                            <button
                              type="button"
                              draggable={false}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => { e.stopPropagation(); toggleSelectLead(lead.id); }}
                              className={`absolute top-2 left-2 z-20 h-4 w-4 rounded flex items-center justify-center border transition-opacity ${
                                isSelected
                                  ? 'opacity-100 bg-blue-500 border-blue-500 text-white'
                                  : 'opacity-0 group-hover:opacity-100 bg-background border-muted-foreground/40 hover:border-blue-500'
                              }`}
                              data-testid={`kanban-select-${lead.id}`}
                              title={isSelected ? 'Deselect' : 'Select lead'}
                            >
                              {isSelected && <Check className="h-3 w-3" />}
                            </button>
                          )}
                          <CardContent className="p-3">
                            {lead.ai_pending_demotion && (
                              <div
                                className="mb-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2"
                                data-testid={`demotion-banner-${lead.id}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-start gap-1.5 mb-1.5">
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                                  <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-tight">
                                    AI suggests marking as <b>{lead.ai_pending_demotion.recommendation}</b>. Approve?
                                  </p>
                                </div>
                                <div className="flex gap-1.5">
                                  <Button
                                    size="sm" variant="destructive"
                                    className="h-6 text-[11px] px-2 flex-1"
                                    disabled={demotionBusy === lead.id}
                                    onClick={(e) => resolveDemotion(lead, 'approve', e)}
                                    data-testid={`demotion-approve-${lead.id}`}
                                  >
                                    {demotionBusy === lead.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Approve'}
                                  </Button>
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-6 text-[11px] px-2 flex-1"
                                    disabled={demotionBusy === lead.id}
                                    onClick={(e) => resolveDemotion(lead, 'decline', e)}
                                    data-testid={`demotion-decline-${lead.id}`}
                                  >
                                    Keep
                                  </Button>
                                </div>
                              </div>
                            )}
                            <div 
                              className="cursor-pointer"
                              onClick={() => handleViewDetails(lead.id)}
                            >
                              {/* Header: Source & Value */}
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-muted-foreground truncate" title={formatLeadSource(lead.source).title}>
                                  {formatLeadSource(lead.source).label}
                                </span>
                                <Badge 
                                  className="text-[10px] px-1.5 py-0"
                                  style={{ backgroundColor: `${statusObj.color}20`, color: statusObj.color }}
                                >
                                  {currency.format(parseFloat(lead.value || 0))}
                                </Badge>
                              </div>
                              
                              {/* Lead Name */}
                              <h4 className="font-medium text-sm line-clamp-2 mb-2">
                                {lead.name}
                              </h4>
                              
                              {/* Company */}
                              <p className="text-xs text-muted-foreground mb-2 truncate">
                                {lead.company || 'No company'}
                              </p>

                              {/* Badges row: AI Score + ICP-Fit + Engagement + Call indicators (horizontal) */}
                              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                {/* AI Score Badge */}
                                {lead.ai_score != null && (
                                  <span data-testid={`kanban-ai-score-${lead.id}`} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                    lead.ai_qualification === 'hot' ? 'bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30' :
                                    lead.ai_qualification === 'warm' ? 'bg-amber-50 text-amber-600 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/30' :
                                    lead.ai_qualification === 'cold' ? 'bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-400 dark:border-blue-500/30' :
                                    'bg-gray-50 text-gray-500 border border-gray-200 dark:bg-white/5 dark:text-gray-400 dark:border-white/10'
                                  }`}>
                                    AI: {lead.ai_score}/100 {lead.ai_qualification}
                                  </span>
                                )}

                                {/* ICP-Fit Badge — Iter 46: 4-tier (Gold/Silver/Bronze) */}
                                {lead.ai_icp_match && lead.ai_icp_match.score != null && (
                                  <span
                                    data-testid={`kanban-icp-fit-${lead.id}`}
                                    className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                      lead.ai_icp_match.score >= 75 ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/30' :
                                      lead.ai_icp_match.score >= 50 ? 'bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-400 dark:border-blue-500/30' :
                                      lead.ai_icp_match.score >= 30 ? 'bg-amber-50 text-amber-600 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/30' :
                                      'bg-red-50 text-red-500 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30'
                                    }`}
                                    title={
                                      lead.ai_icp_match.score >= 75 ? 'Gold — High match (auto-cadence)' :
                                      lead.ai_icp_match.score >= 50 ? 'Silver — Good fit (auto-cadence)' :
                                      lead.ai_icp_match.score >= 30 ? 'Bronze — Needs review before auto-send' :
                                      (lead.ai_icp_match.matching_factors ? lead.ai_icp_match.matching_factors.join(', ') : '')
                                    }
                                  >
                                    <Target className="h-2.5 w-2.5" />
                                    {/* Iter 52: leads <30 still labelled 'Bronze' so the tier system reads
                                        consistently (Gold/Silver/Bronze) — was 'Low' which confused users. */}
                                    {lead.ai_icp_match.score >= 75 ? 'Gold' :
                                     lead.ai_icp_match.score >= 50 ? 'Silver' :
                                     'Bronze'}
                                    {' '}{lead.ai_icp_match.score}%
                                  </span>
                                )}

                                {/* Product-aware Brain: best-fit / declared offering */}
                                {(lead.interested_product_name || lead.recommended_product_name) && (
                                  <span
                                    data-testid={`kanban-product-${lead.id}`}
                                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200 dark:bg-violet-500/15 dark:text-violet-400 dark:border-violet-500/30 max-w-[140px] truncate"
                                    title={lead.interested_product_name ? 'Declared product/service interest' : 'AI best-fit offering'}
                                  >
                                    <Tag className="h-2.5 w-2.5 shrink-0" />
                                    <span className="truncate">{lead.interested_product_name || lead.recommended_product_name}</span>
                                  </span>
                                )}

                                {/* Iter 46+61: Needs-Review chip is now a clickable Popover.
                                    Bronze leads (auto-paused by AI Brain) can be Approved or
                                    Rejected directly from the Kanban — no need to dive into
                                    Lead → Intelligence → Smart Cadence (4 clicks → 1 click). */}
                                {lead.needs_review && (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        data-testid={`kanban-needs-review-${lead.id}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/40 hover:bg-amber-200 dark:hover:bg-amber-500/30 transition-colors cursor-pointer"
                                        title="AI Brain paused outreach — click to Approve or Reject"
                                      >
                                        <AlertTriangle className="h-2.5 w-2.5" />
                                        Review
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      align="start"
                                      sideOffset={6}
                                      className="w-64 p-3 space-y-2"
                                      onClick={(e) => e.stopPropagation()}
                                      data-testid={`kanban-review-popover-${lead.id}`}
                                    >
                                      <div className="space-y-1">
                                        <p className="text-xs font-semibold flex items-center gap-1.5">
                                          <AlertTriangle className="h-3 w-3 text-amber-600" />
                                          Outreach paused
                                        </p>
                                        <p className="text-[11px] text-muted-foreground leading-snug">
                                          AI Brain flagged a low ICP-fit score. Approve to resume, or reject to disqualify.
                                        </p>
                                      </div>
                                      <div className="flex gap-2 pt-1">
                                        <Button
                                          size="sm"
                                          className="flex-1 h-7 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                                          onClick={(e) => handleQuickApprove(lead, e)}
                                          disabled={reviewBusy === lead.id}
                                          data-testid={`kanban-approve-btn-${lead.id}`}
                                        >
                                          {reviewBusy === lead.id
                                            ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                            : null}
                                          Approve
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-7 px-2 text-[11px]"
                                          onClick={(e) => handleQuickReject(lead, e)}
                                          disabled={reviewBusy === lead.id}
                                          data-testid={`kanban-reject-btn-${lead.id}`}
                                        >
                                          ✕ Reject
                                        </Button>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                )}

                                {/* Engagement Score Badge */}
                                {lead.engagement_score != null && (
                                  <span data-testid={`kanban-engagement-${lead.id}`} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                    lead.engagement_level === 'hot' ? 'bg-orange-50 text-orange-600 border border-orange-200 dark:bg-orange-500/15 dark:text-orange-400 dark:border-orange-500/30' :
                                    lead.engagement_level === 'warm' ? 'bg-yellow-50 text-yellow-600 border border-yellow-200 dark:bg-yellow-500/15 dark:text-yellow-400 dark:border-yellow-500/30' :
                                    'bg-sky-50 text-sky-600 border border-sky-200 dark:bg-sky-500/15 dark:text-sky-400 dark:border-sky-500/30'
                                  }`}>
                                    <Activity className="h-2.5 w-2.5" />
                                    {lead.engagement_level === 'hot' ? 'Hot' : lead.engagement_level === 'warm' ? 'Warm' : 'Cold'} · {lead.engagement_score}
                                  </span>
                                )}

                                {/* AI Call indicators */}
                                {callPolling && activeCallLeadId === lead.id && (
                                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/30">
                                    <span className="relative flex h-1.5 w-1.5">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                                    </span>
                                    Calling...
                                  </span>
                                )}
                                {leadSummaries[lead.id]?.length > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const latestSummary = leadSummaries[lead.id][0];
                                      const callRecordId = latestSummary?.call_record_id;
                                      navigate(`/leads/${lead.id}?tab=calls${callRecordId ? `&highlight=${callRecordId}` : ''}`);
                                    }}
                                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200 hover:bg-violet-100 dark:bg-violet-500/15 dark:text-violet-400 dark:border-violet-500/30 dark:hover:bg-violet-500/25 transition-colors"
                                    title="View call summary"
                                    data-testid={`summary-icon-${lead.id}`}
                                  >
                                    <Sparkles className="h-2.5 w-2.5" />
                                    Summary
                                  </button>
                                )}
                              </div>
                              
                              {/* Footer: Contact, Value & Date */}
                              <div className="flex items-center justify-between pt-2 border-t border-dashed border-border text-xs gap-2">
                                {/* Contact icons or Reveal button */}
                                <div className="flex items-center gap-1 shrink-0">
                                  {!lead.contact_revealed && !lead.email && !lead.phone && (lead.apollo_id || lead.provider_id || (lead.source || '').toLowerCase().includes('apollo')) ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 py-0 text-[10px] font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:text-amber-300 dark:hover:bg-amber-500/10"
                                      onClick={(e) => handleRevealContact(lead, e)}
                                      disabled={revealingLeadId === lead.id}
                                      data-testid={`reveal-btn-${lead.id}`}
                                    >
                                      {revealingLeadId === lead.id ? (
                                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                      ) : (
                                        <Lock className="h-3 w-3 mr-1" />
                                      )}
                                      Reveal · $0.065
                                    </Button>
                                  ) : (
                                    <TooltipProvider delayDuration={200}>
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        {lead.contact_revealed && (
                                          <span className="inline-flex items-center text-[10px] text-emerald-500 shrink-0" title="Contact revealed">
                                            <Unlock className="h-2.5 w-2.5" />
                                          </span>
                                        )}
                                        {lead.email ? (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setQuickActionLead(lead);
                                                  setEmailDialogOpen(true);
                                                }}
                                                data-testid={`email-preview-${lead.id}`}
                                              >
                                                <Mail className="h-3 w-3 text-muted-foreground" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom">
                                              <p>{lead.email}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        ) : (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 opacity-30"
                                            disabled
                                          >
                                            <Mail className="h-3 w-3 text-muted-foreground" />
                                          </Button>
                                        )}
                                        {lead.phone ? (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setQuickActionLead(lead);
                                                  setCallDialogOpen(true);
                                                }}
                                                data-testid={`phone-preview-${lead.id}`}
                                              >
                                                <Phone className="h-3 w-3 text-muted-foreground" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom">
                                              <p>{lead.phone}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        ) : (lead.apollo_id || (lead.source || '').toLowerCase().includes('apollo')) ? (
                                          // Iter 59c — partial reveal: phone missing, email present
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-1.5 py-0 text-[10px] text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-500/10"
                                                onClick={(e) => handleRevealContact(lead, e)}
                                                disabled={revealingLeadId === lead.id}
                                                data-testid={`reveal-phone-btn-${lead.id}`}
                                              >
                                                {revealingLeadId === lead.id ? (
                                                  <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : (
                                                  <>
                                                    <Phone className="h-3 w-3 mr-0.5" />
                                                    <Lock className="h-2.5 w-2.5" />
                                                  </>
                                                )}
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom">
                                              <p>Reveal phone · ~8 credits</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        ) : null}
                                        {lead.phone && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 w-5 p-0 shrink-0"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  navigate(`/leads/${lead.id}?tab=activity`);
                                                }}
                                                data-testid={`deal-btn-${lead.id}`}
                                              >
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-muted-foreground">
                                                  <path d="M8 7H4C3.46957 7 2.96086 7.21071 2.58579 7.58579C2.21071 7.96086 2 8.46957 2 9V19C2 19.5304 2.21071 20.0391 2.58579 20.4142C2.96086 20.7893 3.46957 21 4 21H20C20.5304 21 21.0391 20.7893 21.4142 20.4142C21.7893 20.0391 22 19.5304 22 19V9C22 8.46957 21.7893 7.96086 21.4142 7.58579C21.0391 7.21071 20.5304 7 20 7H16M8 7V3.6C8 3.44087 8.06321 3.28826 8.17574 3.17574C8.28826 3.06321 8.44087 3 8.6 3H15.4C15.5591 3 15.7117 3.06321 15.8243 3.17574C15.9368 3.28826 16 3.44087 16 3.6V7M8 7H16" stroke="currentColor" strokeWidth="1.5"/>
                                                </svg>
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom">
                                              <p>View Deals</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                      </div>
                                    </TooltipProvider>
                                  )}
                                </div>

                                {/* Date */}
                                <span className="text-muted-foreground shrink-0">
                                  {lead.created_at ? formatLocal(lead.created_at, 'MMM d') : '-'}
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                      })
                    )}
                    {/* Load More button for paginated Kanban */}
                    {columnHasMore && (
                      <button
                        data-testid={`kanban-load-more-${statusObj.name}`}
                        onClick={() => loadMoreColumn(statusObj.name)}
                        disabled={loadingMoreColumn === statusObj.name}
                        className="w-full py-2 mt-2 text-xs font-medium rounded-lg border border-dashed border-primary/30 text-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
                      >
                        {loadingMoreColumn === statusObj.name ? (
                          <span className="flex items-center justify-center gap-1.5">
                            <Loader2 className="h-3 w-3 animate-spin" /> Loading...
                          </span>
                        ) : (
                          `Load more (${statusLeads.length} of ${columnTotal})`
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}
    </>
  );
});

export default LeadKanbanView;
